import React, { useState, useEffect } from "react";
import {
  subscribeToUserNotifications,
  updateNotificationAsRead,
} from "config/firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import Pagination from "components/Pagination";

const DEFAULT_PAGE_SIZE = 10;

interface NotificationItem {
  avatar: string;
  username: string;
  responderName: string;
  id: string;
  title: string;
  message: string;
  time: string;
  isNew: boolean;
}

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);

  if (minutes < 1) return "0 min";
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""}`;

  const months = Math.floor(days / 30);
  return `${months} month${months > 1 ? "s" : ""}`;
};

const mapNotifications = (raw: any[]): NotificationItem[] =>
  raw
    .map((notif: any) => ({
      avatar: "",
      id: notif.id,
      username: notif.responderName || "Anonymous",
      responderName: notif.responderName || "Anonymous",
      title: notif.title || "",
      message: notif.message || "",
      time: notif.createdAt?.toDate?.().toISOString() || "",
      isNew: !notif.IsRead,
    }))
    .sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    );

const getInitials = (name: string) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) {
    return `${parts[0][0]}${parts[0].slice(-1)}`.toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  // Track the expanded row by id (not list index) so it survives pagination.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const auth = getAuth();

  const totalPages = Math.max(1, Math.ceil(notifications.length / pageSize));

  // Notifications arrive via a realtime subscription, so the list can shrink
  // (e.g. on another device). Keep the current page within range.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedNotifications = notifications.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedId(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setSelectedId(null);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        const unsubscribeNotifications = subscribeToUserNotifications(
          user.uid,
          (fetchedNotifications) => {
            setNotifications(mapNotifications(fetchedNotifications));
          }
        );
        return () => unsubscribeNotifications();
      } else {
        setNotifications([]);
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSelectNotification = async (notification: NotificationItem) => {
    const isExpanding = selectedId !== notification.id;
    setSelectedId(isExpanding ? notification.id : null);

    if (isExpanding && notification.isNew && userId) {
      await updateNotificationAsRead(notification.id, userId);

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, isNew: false } : n,
        ),
      );
    }
  };

  return (
    <div>
      <h1 className="text-xl md:text-2xl font-bold text-grease-100 mb-4 md:mb-6">Notifications</h1>

      <div className="w-full">
        {/* Left - Notification List */}
        <div className="w-full">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {notifications.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {paginatedNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleSelectNotification(notification)}
                    className={`flex items-start gap-3 px-5 py-4 cursor-pointer transition-colors ${
                      selectedId === notification.id
                        ? "border-l-2 border-l-light-yellow bg-yellow-50/50"
                        : "border-l-2 border-l-transparent hover:bg-gray-50"
                    }`}
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 text-gray-600 text-sm font-bold">
                        {getInitials(notification.responderName)}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-grease-100 truncate">
                          {notification.title || notification.responderName}
                        </p>
                        {notification.isNew && (
                          <span className="flex-shrink-0 w-2 h-2 rounded-full bg-light-yellow"></span>
                        )}
                      </div>
                      <p
                        className={`text-xs text-gray-500 mt-1 ${
                          selectedId === notification.id
                            ? "whitespace-pre-wrap break-words"
                            : "truncate"
                        }`}
                      >
                        {notification.message ||
                          `${notification.responderName} has answered your request`}
                      </p>
                      {selectedId === notification.id && (
                        <p className="text-[11px] text-gray-400 mt-2">
                          From {notification.responderName}
                          {notification.time
                            ? ` • ${new Date(notification.time).toLocaleString()}`
                            : ""}
                        </p>
                      )}
                    </div>

                    {/* Time & Arrow */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-400">
                        {notification.time ? formatTimeAgo(notification.time) : ""}
                      </span>
                      <i
                        className={`st-arrow-right text-xs text-gray-300 transition-transform ${
                          selectedId === notification.id ? "rotate-90" : ""
                        }`}
                      ></i>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-60">
                <p className="text-gray-400 text-sm">No notifications</p>
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalCount={notifications.length}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              showTotal
            />
          )}
        </div>

        {/* Right - Notification Detail (hidden for now) */}
        {/* <div className="flex-1 min-w-0">
          {selectedNotification ? (
            <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6">
              {/* Header with avatar and name *}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-200 text-gray-600 text-sm font-bold">
                    {getInitials(selectedNotification.responderName)}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-grease-100">
                      {selectedNotification.responderName}
                    </p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600 p-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <circle cx="10" cy="4" r="1.5" />
                    <circle cx="10" cy="10" r="1.5" />
                    <circle cx="10" cy="16" r="1.5" />
                  </svg>
                </button>
              </div>

              {/* Message *}
              <div className="mt-6">
                <p className="text-sm text-gray-600 leading-relaxed break-words">
                  {selectedNotification.message}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 flex flex-col items-center justify-center min-h-[300px] h-full p-6">
              <img
                src={NotifImg}
                alt="No notification selected"
                className="w-full max-w-[200px] h-auto"
              />
              <p className="text-grease-100 font-semibold text-base mt-4">You're all caught up</p>
              <p className="text-gray-400 text-sm mt-1 text-center">
                Choose a notification to see more<br />details here
              </p>
            </div>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default Notifications;
